"use client";

import clsx from "clsx";
import type { ComponentProps } from "react";

import { Breadcrumbs } from "@features/navigations/breadcrumbs";

import { Icon } from "@shared/ui/icon";
import { StyledButton } from "@shared/ui/styled-button";
import { TrailNumber } from "@shared/ui/trail-number";
import { Body, Heading } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import type { TrailPageType } from "../../model";
import { TrailInfoListSection } from "./trail-info-list";

import s from "./content.module.scss";

export type ContentProps = ComponentProps<"div"> & {
  className?: string;
  trail: TrailPageType;
};

export const Content = (props: ContentProps) => {
  const { className, trail } = props;

  const mods = mod(s, {
    difficulty: trail.difficulty || "medium",
  });

  return (
    <div className={clsx(s.root, className, mods)}>
      <Breadcrumbs className={s.breadcrumbs} />
      <div className={s.header}>
        {trail.trailNumber && (
          <TrailNumber
            className={s.number}
            variant={trail.difficulty || "medium"}
            size="64"
          >
            {trail.trailNumber}
          </TrailNumber>
        )}
        <Heading level={2} tag="h1" className={s.title}>
          {trail.title}
        </Heading>
      </div>
      <TrailInfoListSection
        trailStatus={trail.trailStatus}
        length={trail.length}
        workingHours={trail.workingHours}
        ascentTime={trail.ascentTime}
        descentTime={trail.descentTime}
        capacity={trail.capacity}
        difficulty={trail.difficulty}
        start={trail.start}
        startDescription={trail.startDescription}
        end={trail.end}
        endDescription={trail.endDescription}
        className={s.infoList}
      />
      {trail.description && (
        <Body size="primary" weight="regular" className={s.description}>
          {trail.description}
        </Body>
      )}
      <div className={s.btns}>
        {trail?.trackFiles &&
          trail.trackFiles?.length > 0 &&
          trail.trackFiles.map((file) => (
            <StyledButton
              target="_blank"
              href={file?.file?.url}
              size="s"
              className={s.doc}
              key={`file_${file.id}`}
            >
              <Icon className={s.mapBtnIcon} name="download" size={24} />
              <Body size="uppercase-small" weight="semiBold">
                {file.title}
              </Body>
            </StyledButton>
          ))}
        {/* TODO: Вывести ссылки на камеры и карты */}
        {/*<StyledButton size="s" href="/web-cams">*/}
        {/*  <Icon className={s.mapBtnIcon} name="webcamera" size={24} />*/}
        {/*</StyledButton>*/}
        <StyledButton size="s" href="/map" aria-label="Открыть карту курорта">
          <Icon className={s.mapBtnIcon} name="map" size={24} />
        </StyledButton>
      </div>
    </div>
  );
};

Content.displayName = "Content";
